import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopPage = () => {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(category || 'all');

  const categories = [
    { name: 'All', slug: 'all' },
    { name: 'Makeup', slug: 'makeup' },
    { name: 'Jewelry', slug: 'jewelry' },
    { name: 'Body', slug: 'body' },
    { name: 'Nails', slug: 'nails' },
    { name: 'Hair', slug: 'hair' },
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let url = `${API}/products`;
        const params = new URLSearchParams();
        
        const catToUse = category || activeCategory;
        if (catToUse && catToUse !== 'all') {
          params.append('category', catToUse);
        }
        
        if (searchParams.get('featured') === 'true') {
          params.append('featured', 'true');
        }
        
        if (params.toString()) {
          url += `?${params.toString()}`;
        }
        
        const res = await axios.get(url);
        setProducts(res.data);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (category) {
      setActiveCategory(category);
    }
    fetchProducts();
  }, [category, searchParams, activeCategory]);

  const handleCategoryChange = (slug) => {
    setActiveCategory(slug);
    window.history.pushState({}, '', slug === 'all' ? '/shop' : `/shop/${slug}`);
  };

  const categoryTitle = category 
    ? category.charAt(0).toUpperCase() + category.slice(1) 
    : 'All Products';

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-heading text-4xl md:text-5xl mb-4" data-testid="shop-title">
            {searchParams.get('featured') ? 'Featured Collection' : categoryTitle}
          </h1>
          <p className="text-muted-foreground">
            {products.length} {products.length === 1 ? 'product' : 'products'}
          </p>
        </motion.div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-12">
          {categories.map(cat => (
            <Button
              key={cat.slug}
              variant={activeCategory === cat.slug ? 'default' : 'outline'}
              className={`text-xs tracking-widest uppercase ${
                activeCategory === cat.slug
                  ? 'bg-gold text-black hover:bg-gold-dark'
                  : 'border-white/20 text-muted-foreground hover:border-gold hover:text-gold'
              }`}
              onClick={() => handleCategoryChange(cat.slug)}
              data-testid={`filter-${cat.slug}`}
            >
              {cat.name}
            </Button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-secondary/30 mb-4" />
                <div className="h-4 bg-secondary/30 w-3/4 mb-2" />
                <div className="h-4 bg-secondary/30 w-1/4" />
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="products-grid">
            {products.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-muted-foreground">No products found in this category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopPage;
